import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Award, TrendingUp, ChevronRight, Star, Zap, Crown } from "lucide-react";
import axios from "axios";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const tierConfig = {
  starter: { 
    label: "Starter", 
    color: "#94A3B8", 
    icon: Star,
    benefits: ["Basic sessions", "Standard rates", "Email support"],
    requirements: "New therapist"
  },
  pro: { 
    label: "Pro", 
    color: "#60A5FA",
    icon: Zap,
    benefits: ["Priority booking", "+10% rates", "Phone support", "Badge display"],
    requirements: "80% performance, 50+ sessions"
  },
  elite: { 
    label: "Elite", 
    color: "#F59E0B",
    icon: Award,
    benefits: ["Premium booking", "+20% rates", "Dedicated support", "Featured profile"],
    requirements: "90% performance, 100+ sessions"
  },
  master: { 
    label: "Master", 
    color: "#1E293B",
    icon: Crown,
    benefits: ["VIP access", "+30% rates", "Personal manager", "Speaking opportunities"],
    requirements: "95% performance, 200+ sessions"
  },
};

export default function AdminTiers() {
  const [therapists, setTherapists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/admin/therapists`, { withCredentials: true });
        setTherapists(response.data);
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const tierCounts = {
    starter: therapists.filter(t => t.tier === "starter").length,
    pro: therapists.filter(t => t.tier === "pro").length,
    elite: therapists.filter(t => t.tier === "elite").length,
    master: therapists.filter(t => t.tier === "master").length,
  };

  const pieData = Object.entries(tierCounts).map(([key, value]) => ({
    name: tierConfig[key].label,
    value,
    color: tierConfig[key].color
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-tiers">
      {/* Tier Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {Object.entries(tierConfig).map(([key, config]) => {
          const Icon = config.icon;
          return (
            <Card key={key} className="kpi-card overflow-hidden">
              <div className={`h-1`} style={{ backgroundColor: config.color }}></div>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{config.label}</p>
                    <p className="text-3xl font-heading font-bold text-slate-900">{tierCounts[key]}</p>
                  </div>
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${config.color}20` }}
                  >
                    <Icon className="w-6 h-6" style={{ color: config.color }} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tier Distribution */}
        <Card className="chart-container">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Tier Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value, name) => [value, name]}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex justify-center gap-6 mt-4">
              {pieData.map((tier) => (
                <div key={tier.name} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: tier.color }}></div>
                  <span className="text-sm text-slate-600">{tier.name}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Tier Benefits */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Tier Benefits & Requirements</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(tierConfig).map(([key, config]) => {
                const Icon = config.icon;
                return (
                  <div key={key} className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3 mb-3">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${config.color}20` }}
                      >
                        <Icon className="w-5 h-5" style={{ color: config.color }} />
                      </div>
                      <div>
                        <p className="font-heading font-semibold" style={{ color: config.color }}>
                          {config.label}
                        </p>
                        <p className="text-xs text-slate-500">{config.requirements}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {config.benefits.map((benefit, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">
                          {benefit}
                        </Badge>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Therapists by Tier */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">Tier Progression</CardTitle>
        </CardHeader>
        <CardContent>
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Therapist</TableHead>
                <TableHead>Current Tier</TableHead>
                <TableHead>Performance</TableHead>
                <TableHead>Sessions</TableHead>
                <TableHead>Progress to Next</TableHead>
                <TableHead>Next Tier</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {therapists.slice(0, 10).map((th) => {
                const currentTier = tierConfig[th.tier];
                const tierOrder = ["starter", "pro", "elite", "master"];
                const currentIndex = tierOrder.indexOf(th.tier);
                const nextTier = currentIndex < 3 ? tierConfig[tierOrder[currentIndex + 1]] : null;
                const progressToNext = nextTier ? Math.min((th.performance_score / 95) * 100, 100) : 100;
                
                return (
                  <TableRow key={th.therapist_id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="w-8 h-8">
                          <AvatarImage src={th.picture} />
                          <AvatarFallback className="bg-primary text-white text-sm">
                            {th.name.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium">{th.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        className="flex items-center gap-1 w-fit"
                        style={{ 
                          backgroundColor: `${currentTier.color}20`,
                          color: currentTier.color
                        }}
                      >
                        {currentTier.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">{th.performance_score}%</span>
                    </TableCell>
                    <TableCell>
                      <span>{th.total_sessions}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress value={progressToNext} className="w-20 h-2" />
                        <span className="text-xs text-slate-500">{Math.round(progressToNext)}%</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {nextTier ? (
                        <div className="flex items-center gap-1 text-sm text-slate-600">
                          <ChevronRight className="w-4 h-4" />
                          <span style={{ color: nextTier.color }}>{nextTier.label}</span>
                        </div>
                      ) : (
                        <Badge className="bg-emerald-100 text-emerald-700">Max Tier</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
